'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface EventModalProps {
  event: {
    id: string;
    title: string;
    date: string;
    time: string;
    location: string;
    type: 'preparation' | 'celebration' | 'community' | 'global';
    description: string;
    organizer: string;
    attendees: number;
    image: string;
    alt: string;
    timezone: string;
    fullDescription: string;
    resources: string[];
  } | null;
  isOpen: boolean;
  onClose: () => void;
  onRegister: (eventId: string) => void;
  onShare: (eventId: string) => void;
}

const EventModal: React.FC<EventModalProps> = ({
  event,
  isOpen,
  onClose,
  onRegister,
  onShare,
}) => {
  if (!isOpen || !event) return null;

  const getEventTypeColor = () => {
    switch (event.type) {
      case 'preparation':
        return 'bg-peaceful-teal text-white';
      case 'celebration':
        return 'bg-accent text-accent-foreground';
      case 'community':
        return 'bg-secondary text-secondary-foreground';
      case 'global':
        return 'bg-primary text-primary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getEventTypeLabel = () => {
    switch (event.type) {
      case 'preparation':
        return 'Preparation';
      case 'celebration':
        return 'Celebration';
      case 'community':
        return 'Community';
      case 'global':
        return 'Global Unity';
      default:
        return 'Event';
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header Image */}
          <div className="relative h-64 overflow-hidden rounded-t-lg">
            <AppImage
              src={event.image}
              alt={event.alt}
              className="w-full h-full object-cover"
            />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 bg-white/90 rounded-full hover:bg-white transition-colors duration-200"
              aria-label="Close modal"
            >
              <Icon name="XMarkIcon" size={24} />
            </button>
            <div className="absolute top-4 left-4">
              <span className={`px-3 py-1 rounded-full text-xs font-cta font-bold ${getEventTypeColor()}`}>
                {getEventTypeLabel()}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
              {event.title}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 text-foreground">
                <Icon name="CalendarIcon" size={20} />
                <span className="font-body">{event.date}</span>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <Icon name="ClockIcon" size={20} />
                <span className="font-body">{event.time} ({event.timezone})</span>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <Icon name="MapPinIcon" size={20} />
                <span className="font-body">{event.location}</span>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <Icon name="UserGroupIcon" size={20} />
                <span className="font-body">{event.attendees} attending</span>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-headline font-bold text-foreground mb-3">
                About This Event
              </h3>
              <p className="text-foreground font-body leading-relaxed whitespace-pre-line">
                {event.fullDescription}
              </p>
            </div>

            {event.resources.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-headline font-bold text-foreground mb-3">
                  Resources
                </h3>
                <ul className="space-y-2">
                  {event.resources.map((resource, index) => (
                    <li key={index} className="flex items-center gap-2 text-foreground font-body">
                      <Icon name="DocumentTextIcon" size={16} />
                      <span>{resource}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-between pt-6 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Organized by <span className="font-bold text-foreground">{event.organizer}</span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => onShare(event.id)}
                  className="px-4 py-2 border border-border rounded-md font-cta text-sm font-bold hover:bg-muted transition-colors duration-200 flex items-center gap-2"
                >
                  <Icon name="ShareIcon" size={18} />
                  Share
                </button>
                <button
                  onClick={() => {
                    onRegister(event.id);
                    onClose();
                  }}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-md font-cta text-sm font-bold hover:scale-105 transition-transform duration-300"
                >
                  Register Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EventModal;